// System imports
import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './services/reactQueryConfig';

// Page components
import HomePage from './pages/home';
import PricingPage from './pages/pricing';
import PrivacyPage from './pages/privacy/PrivacyPage';
import TermsPage from './pages/terms/TermsPage';
import CancelPolicyPage from './pages/cancel-policy/CancelPolicyPage';
import Landing from './pages/landing/Landing';
import LoginPage from './pages/login/LoginPage';
import AppLayout from './components/layout/AppLayout';
import SettingsPage from './pages/settings/SettingsPage';
import DataManagementPage from './pages/data_management';
import OnboardingBilling from './pages/onboarding/OnboardingBilling';
import OnboardingUserInfo from './pages/onboarding/OnboardingUserInfo';

// Context providers
import { ThemeProvider } from './context/ThemeContext';
import { ProjectProvider } from './context/ProjectContext';
import { ChatProvider } from './context/ChatContext';
import { ViewerProvider } from './context/ViewerContext';
import { AccessControlProvider } from './context/AccessControlContext';

// Import ErrorBoundary
import ErrorBoundary from './components/ErrorBoundary'; 
// Import ErrorBannerManager
import ErrorBannerManager from './components/common/ErrorBannerManager';
// Import MobilePerformanceMonitor
import MobilePerformanceMonitor from './components/MobilePerformanceMonitor';

// Services
import userService from './services/userServices';
import { setOnAuthErrorCallback, setAppInitialized } from './services/requestServices';

// Styles
import './styles.css';

export const AuthContext = createContext(null);
export function useAuthContext() {
  return useContext(AuthContext);
}

function App() {
  // Initialize global flag as false
  if (typeof window !== 'undefined') {
    window.__APP_INITIALIZED__ = false;
  }
  
  // Use lazy initialization to check sessionStorage for sessionChecked flag
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // On mount, check if we have a cached auth state
    const wasAuthed = localStorage.getItem('wasAuthed');
    return wasAuthed ? true : null;
  });
  
  const [sessionChecked, setSessionChecked] = useState(() => {
    // Persist session check across remounts using sessionStorage (cleared on tab close)
    return sessionStorage.getItem('sessionChecked') === 'true';
  });

  // (A) Optionally store user info if session_status returns "user"
  const [userProfile, setUserProfile] = useState(null);

  // (1) On mount => call session_status endpoint (NOT a 401-based user_info)
  useEffect(() => {
    async function doCheck() {
      try {
        // Check if this is a real page navigation (full reload) vs hot reload
        const isPageNavigation = !sessionStorage.getItem('appMounted');
        
        // Mark that the app has mounted in this session
        sessionStorage.setItem('appMounted', 'true');
        
        // Always check session on page navigation/reload
        // Only skip on ErrorBoundary recovery (when sessionChecked is already set)
        const skipCheck = !isPageNavigation && sessionStorage.getItem('sessionChecked') === 'true';
        
        if (skipCheck) {
          console.debug('[Auth] Skipping redundant session check (ErrorBoundary recovery)');
          setSessionChecked(true);
          setAppInitialized();
          return;
        }
        
        // First check if there was a previous auth
        const wasAuthed = localStorage.getItem('wasAuthed');
        console.debug('[Auth] Initial localStorage wasAuthed =', wasAuthed);
        
        // If we have a cached auth state, use it while we check with the server
        // This provides instant UI feedback
        if (wasAuthed) {
          console.debug('[Auth] Setting initial auth state to true based on localStorage');
          setIsAuthenticated(true);
          // Mark as checked immediately to prevent loading delays
          setSessionChecked(true);
          sessionStorage.setItem('sessionChecked', 'true');
          setAppInitialized();
        }
        
        // Then verify with the server in parallel (don't block rendering)
        console.debug('[Auth] Verifying session with server...');
        const resp = await userService.sessionStatus();
        console.debug('[Auth] Server session status response:', resp);
        
        if (resp.isLoggedIn) {
          console.debug('[Auth] Server confirms user is logged in');
          setIsAuthenticated(true);
          setUserProfile(resp.user || null);
          localStorage.setItem('wasAuthed', 'true');
        } else {
          console.debug('[Auth] Server confirms user is NOT logged in');
          setIsAuthenticated(false);
          setUserProfile(null);
          localStorage.removeItem('wasAuthed');
        }
        
        // Ensure flags are set even if we already set them above
        if (!wasAuthed) {
          setSessionChecked(true);
          sessionStorage.setItem('sessionChecked', 'true');
          setAppInitialized();
        }
      } catch (err) {
        console.warn('[Auth] Session status check failed:', err);
        setIsAuthenticated(false);
        setUserProfile(null);
        localStorage.removeItem('wasAuthed');
        // Still mark as checked to prevent infinite loading
        setSessionChecked(true);
        sessionStorage.setItem('sessionChecked', 'true');
        setAppInitialized();
      }
    }
    doCheck();
  }, []);

  // (2) If the server returns 401 for any other calls => forcibly log out
  useEffect(() => {
    function handleUnauthorized() {
      console.warn('[Auth] 401 => forcing logout');
      // Use a more stable approach to prevent cascading re-renders
      // Only update if we're actually logged in
      const wasAuthed = localStorage.getItem('wasAuthed');
      if (wasAuthed) {
        setIsAuthenticated(false);
        setUserProfile(null);
        localStorage.removeItem('wasAuthed');
        sessionStorage.removeItem('sessionChecked');
      }
    }
    setOnAuthErrorCallback(handleUnauthorized);
    return () => setOnAuthErrorCallback(() => {});
  }, []);

  // Only show loading screen if we're actually checking AND on a protected route
  // Don't show it on public pages like home, pricing, etc.
  if (!sessionChecked) {
    console.debug('[Auth] Rendering loading screen while checking session');
    const currentPath = window.location.pathname;
    const isPublicPage = currentPath === '/' || 
                         currentPath === '/home' || 
                         currentPath === '/pricing' ||
                         currentPath === '/privacy' ||
                         currentPath === '/terms' ||
                         currentPath === '/login';
    
    // For public pages, render them immediately without waiting for session check
    if (isPublicPage) {
      // Session will check in background, but render the page immediately
      console.debug('[Auth] Public page - rendering without waiting for session check');
    } else {
      // For protected pages, show loading screen
      return (
        <div style={{ textAlign: 'center', marginTop: '3rem', color: '#888' }}>
          Checking session...
        </div>
      );
    }
  }

  // Final logged state for debugging
  console.debug('[Auth] Final auth state before rendering routes:', isAuthenticated);

  // Provide the auth context
  const authValue = {
    isAuthenticated,
    setIsAuthenticated,
    userProfile,       // optional
    setUserProfile,    // optional
  };

  return (
    // Wrap with ErrorBoundary
    <ErrorBoundary>
      <AuthContext.Provider value={authValue}>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider>
            <AccessControlProvider>
              {/* Add ErrorBannerManager here so it has access to theme context */}
              <ErrorBannerManager />
              {/* Add MobilePerformanceMonitor to prevent crashes on mobile */}
              <MobilePerformanceMonitor />
              <BrowserRouter>
                <Routes>
                  {/* Home page - public marketing page */}
                  <Route
                    path="/"
                    element={<HomePage />}
                  />
                  <Route
                    path="/home"
                    element={<HomePage />}
                  />
                  {/* Pricing page - public pricing page */}
                  <Route
                    path="/pricing"
                    element={<PricingPage />}
                  />
                  {/* Privacy page - public privacy statement */}
                  <Route
                    path="/privacy"
                    element={<PrivacyPage />}
                  />
                  {/* Terms page - public terms of use */}
                  <Route
                    path="/terms"
                    element={<TermsPage />}
                  />
                  {/* Cancellation policy page - public cancellation policy */}
                  <Route
                    path="/cancel-policy"
                    element={<CancelPolicyPage />}
                  />
                  {/* Onboarding billing page - for new users */}
                  <Route
                    path="/onboarding/billing"
                    element={
                      isAuthenticated ? <OnboardingBilling /> : <Navigate to="/login" replace />
                    }
                  />
                  {/* Onboarding user info page - after successful checkout */}
                  <Route
                    path="/onboarding/user_info"
                    element={
                      isAuthenticated ? <OnboardingUserInfo /> : <Navigate to="/login" replace />
                    }
                  />
                  {/* Landing page - if logged in => show landing, else => show <LoginPage/> */}
                  <Route
                    path="/landing"
                    element={
                      isAuthenticated ? <Landing /> : <Navigate to="/login" replace />
                    }
                  />
                  {/* Login page - if logged in => go /landing, else => show <LoginPage/> */}
                  <Route
                    path="/login"
                    element={
                      isAuthenticated ? <Navigate to="/landing" replace /> : <LoginPage />
                    }
                  />
                  {/* Projects page (workspace) => require auth */}
                  <Route
                    path="/projects"
                    element={
                      isAuthenticated ? (
                        <ProjectProvider>
                          <ChatProvider>
                            <ViewerProvider>
                              <AppLayout />
                            </ViewerProvider>
                          </ChatProvider>
                        </ProjectProvider>
                      ) : (
                        <Navigate to="/login" replace />
                      )
                    }
                  />
                  
                  {/* settings => require auth */}
                  <Route
                    path="/settings/*"
                    element={
                      isAuthenticated ? (
                        <SettingsPage />
                      ) : (
                        <Navigate to="/login" replace />
                      )
                    }
                  />
                  
                  {/* data-management => require auth */}
                  <Route
                    path="/data-management/*"
                    element={
                      isAuthenticated ? (
                        <DataManagementPage />
                      ) : (
                        <Navigate to="/login" replace />
                      )
                    }
                  />
                  
                  {/* fallback */}
                  <Route path="*" element={<div>404 Not Found</div>} />
                </Routes>
              </BrowserRouter>
            </AccessControlProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </AuthContext.Provider>
    </ErrorBoundary>
  );
}

export default App;
